import React, { useEffect, useState, useRef } from 'react';
import { ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
import gsap from 'gsap';

const SegmentedScaleComponent = ({
  name,
  question,
  onChange,
  onNext,
  setValidationState,
  registerValidation,
  isMobile,
  values,
  prop,
}) => {
  const [selected, setSelected] = useState('');
  const hasUserInteracted = useRef(false);
  const buttonRefs = useRef([]);

  // GSAP bounce
  const gs_bounce = (target, strength = 0.85) => {
    return new Promise((resolve) => {
      const tl = gsap.timeline({ onComplete: resolve });
      tl.to(target, {
        scale: strength,
        duration: 0.15,
        ease: 'Power3.easeInOut',
      }).to(target, {
        duration: 1.3,
        scale: 1,
        ease: 'elastic.out(1.75, 0.4)',
      });
    });
  };

  useEffect(() => {
    const initialValue = values?.[name] || '';
    setSelected(initialValue);
    question.value = initialValue;
  }, [question, values, name]);

  useEffect(() => {
    if (registerValidation) {
      registerValidation(name, () => validate(values?.[name]));
    }
  }, [values, registerValidation, name]);

  const validate = (val) => {
    const isValid = !!val && val.trim?.() !== '';
    setValidationState?.(name, isValid);
    return isValid;
  };

  const validateGroup = (fieldName, fieldValue) => {
    const group = prop.questions.find((g) =>
      Object.keys(g).includes(fieldName)
    );
    if (!group) return false;

    return Object.entries(group).every(([key]) => {
      if (key === 'layout') return true;
      const val = key === fieldName ? fieldValue : values?.[key];
      if (typeof val === 'string') return val.trim().length > 0;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === 'object' && val !== null)
        return Object.values(val).every((v) => v && v !== '');
      return val !== undefined && val !== null;
    });
  };

  const handleClick = (option, index) => {
    hasUserInteracted.current = true;
    setSelected(option);
    question.value = option;
    onChange?.(name, option);

    const el = buttonRefs.current[index];
    if (el) gs_bounce(el);

    const isValid = validate(option);
    const shouldAutoNext =
      isValid &&
      !question.disableAutoNext &&
      !prop.buttonLabel &&
      validateGroup(name, option);

    if (shouldAutoNext) onNext?.(option); // ← pass the selected option
  };

  const isVertical = isMobile;
  const buttonGroupDirection = isVertical ? 'column' : 'row';

  return (
    <Box
      sx={{
        maxWidth: isMobile ? '100%' : '712px',
        boxSizing: 'border-box',
        width: '100%',
        mx: isMobile ? 'auto' : 'unset',
        //border: '1px solid red',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          height: isMobile ? '350px' : '53px',
        }}
      >
        <ToggleButtonGroup
          value={selected}
          exclusive
          orientation={buttonGroupDirection}
          sx={{
            display: 'flex',
            flexDirection: buttonGroupDirection,
            width: isMobile ? '95px' : 'auto',
            height: isMobile ? '100%' : 'auto',
            overflow: 'hidden',
            border: 'none', // no outer border
          }}
        >
          {question.options.map((option, index) => {
            const isFirst = index === 0;
            const isLast = index === question.options.length - 1;
            const itemCount = question.options.length;
            const buttonHeight = isMobile
              ? `calc(350px / ${itemCount})`
              : 'auto';

            // Collapse the inner seams: overlap borders by 1px
            const seamCollapseStyle = isMobile
              ? { mt: isFirst ? 0 : '-1px' } // vertical stack
              : { ml: isFirst ? 0 : '-1px' }; // horizontal row

            return (
              <Box
                key={index}
                sx={{
                  overflow: 'hidden',
                  width: isMobile ? '100%' : '712px',
                  height: buttonHeight,
                  ...seamCollapseStyle,
                }}
              >
                <ToggleButton
                  ref={(el) => (buttonRefs.current[index] = el)}
                  value={option}
                  selected={selected === option}
                  onClick={() => handleClick(option, index)}
                  sx={(theme) => ({
                    padding: '12px 24px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    fontSize: isMobile ? '0.8125rem' : '1rem',
                    lineHeight: '1.5rem',
                    textAlign: 'center',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                    zIndex: selected === option ? 5 : 'auto',
                    color: '#162F3B',
                    textTransform: 'none',
                    width: '100%',
                    height: '100%',
                    border: '1px solid #E3E6E8', // 1px for crispness
                    borderRadius: 0,
                    ...(isFirst &&
                      isMobile && {
                        borderTopLeftRadius: '12px',
                        borderTopRightRadius: '12px',
                      }),
                    ...(isLast &&
                      isMobile && {
                        borderBottomLeftRadius: '12px',
                        borderBottomRightRadius: '12px',
                      }),
                    ...(isFirst &&
                      !isMobile && {
                        borderTopLeftRadius: '16px',
                        borderBottomLeftRadius: '16px',
                      }),
                    ...(isLast &&
                      !isMobile && {
                        borderTopRightRadius: '16px',
                        borderBottomRightRadius: '16px',
                      }),
                    '&.Mui-selected': {
                      backgroundColor: '#C7CA00',
                      borderColor: theme.palette.component?.valid || '#C7CA00',
                      color: '#1C2F3A',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#C7CA00',
                      color: '#1C2F3A',
                    },
                    '&:hover': {
                      backgroundColor: '#f2f2f2',
                    },
                  })}
                >
                  {option}
                </ToggleButton>
              </Box>
            );
          })}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};

export default SegmentedScaleComponent;
